import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ProfileItem } from '@/types/interfaces'
import { usePopup } from '@/providers/PopupContext'
import useProfile from '@/hooks/useProfile'
import { TabsList } from '@/components/TabsList/TabsList'
import { Loader, Subscriptions, Profile } from './components'
import { ROUTES } from '@/constants/routes'
import { TokenService } from '@/services/TokenService'
import { updateSentryUser } from '@/services/analytics/sentry-init'

import avatarDefault from '@/assets/images/svg/avatar_default.svg'
import { Header } from '@/components/Header/Header'
import { TABS, TabItem, tabsList } from '@/components/TabsList/types'

type ProfilePageProps = {
  baseTab: TabItem
}

const ProfilePage = ({ baseTab }: ProfilePageProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setOpen, setPopupContent } = usePopup()
  const [userName, setUserName] = useState<string>('')
  const [avatar, setAvatar] = useState<string | null>(null)
  const [profileItems, setProfileItems] = useState<ProfileItem[]>([])
  const [paymentPlan, setPaymentPlan] = useState<any>({})
  const [paymentUpsale, setPaymentUpsale] = useState<any>({})

  const openPopup = () => {
    setPopupContent({
      title: `${t('opps')}`,
      description: `${t('contactUsEmail')}`,
      buttonText: `${t('ok')}`,
      onButtonClick: () => {
        TokenService.clearTokens()
        navigate(ROUTES.LOGIN)
      },
    })
    setOpen(true)
  }

  const { data, isLoading, error } = useProfile()

  useEffect(() => {
    if (error) openPopup()
  }, [error])

  useEffect(() => {
    if (data) {
      setUserName(data?.userName)
      setAvatar(data?.avatar)
      setPaymentPlan(data?.paymentPlan)
      setProfileItems(data?.profileItems)
      setPaymentUpsale(data?.upSale)

      const profileMap = data.profileItems.reduce(
        (
          acc: { id: string; email: string; name: string },
          item: { key: string; label: string; value: string; class: string },
        ) => {
          acc[item.key] = item.value
          return acc
        },
        {} as Record<string, string>,
      )

      updateSentryUser({
        ...(profileMap.id && { id: profileMap.id }),
        ...(profileMap.email && { email: profileMap.email }),
        ...(profileMap.name && { username: profileMap.name }),
      })
    }
  }, [data])

  const tabComponents = {
    [TABS.PROFILE]: <Profile items={profileItems} />,
    [TABS.SUBSCRIPTIONS]: <Subscriptions paymentPlan={paymentPlan} paymentUpsale={paymentUpsale} />,
    // [TABS.PAYMENTS]: <Payments />,
  }

  return (
    <div className="w-full relative flex flex-col h-full overflow-y-auto overflow-x-hidden pb-20">
      <div className="bg-white p-4 rounded-b-[25px]">
        <Header title={t('manageSubscription' as any)} />
        <div className="flex flex-col items-center w-full">
          <div className="shrink-0">
            <img
              src={avatar && !avatar?.includes('undefined') ? avatar : avatarDefault}
              alt={t('avatar')}
              height={86}
              width={86}
              className="rounded-full object-cover h-[86px] w-[86px]"
            />
          </div>
          <h3 className="font-outfit min-h-7 mt-3 font-semibold text-center transition-all duration-300 text-xl2 capitalize">
            {userName}
          </h3>
        </div>

        <TabsList list={tabsList} activeTab={baseTab} />
      </div>

      <div className="mt-4">{isLoading ? <Loader /> : (tabComponents[baseTab] ?? null)}</div>
    </div>
  )
}

export default ProfilePage
